import React, { useEffect, useState } from 'react';
import { Button, Image, Modal } from 'react-bootstrap';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { FiPlus, FiXCircle, FiCheckCircle } from 'react-icons/fi';
import UserDepartament from '../components/UserDepartament';
import ListColor from '../components/ListColor';
import ModalMessage from '../components/ModalMessage';
import ModalYesNo from '../components/ModalYesNo';
import { useToast } from '../hooks/toast';

import api from '../services/apiClient';

import PencilIcon from '../assets/icon/pencil.svg';
import DeleteIcon from '../assets/icon/delete.svg';
import UserGroup from './UserGroup';
import UserAdd from './UserAdd';
import UserDepartamentForm from './UserDepartament';
import UserSupervisor from './UserSupervisor';
import { getInitials } from '../services/function';
import IDepartamento from '../interfaces/IDepartament';
import IUserGroup from '../interfaces/IUserGroup';
import IUserDepartament from '../interfaces/IUserDepartament';
import IUserSupervisor from '../interfaces/IUserSupervisor';

interface User {
  seqUsuario: number;
  usuario: string;
  nome: string;
  email: string;
  changePassword?: boolean;
  password: string;
  confirmPassword?: string;
  ativo: string;
  administrador: string;
  cor: string;
}

const extractApiMessage = (data: any): string => {
  if (typeof data === 'string') return data;
  if (typeof data?.message === 'string') return data.message;
  if (typeof data?.Message === 'string') return data.Message;
  return '';
};

const shouldOfferDisable = (message: string): boolean => {
  if (!message) return false;

  const normalized = message
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

  return (
    normalized.includes('existem mensagens vinculadas') &&
    normalized.includes('deseja desativa')
  );
};

const stickyHeaderCellStyle: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 1,
  backgroundColor: '#D9D9D9',
};

const User: React.FC<any> = () => {
  const { addToast } = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [userSelected, setUserSelected] = useState<User>({
    seqUsuario: 0,
  } as User);
  const [userInativar, setUserInativar] = useState<User>({
    seqUsuario: 0,
  } as User);

  const token = localStorage.getItem('@LiveChat:token');

  const [userDepartamentLinked, setUserDepartamentLinked] = useState(
    [] as IDepartamento[]
  );
  const [departamentColor, setDepartamentColor] = useState<string[]>([]);
  const [userColor, setUserColor] = useState('');
  const [key, setKey] = useState('user');
  const [userGroups, setUserGroups] = useState<IUserGroup[]>([]);
  const [userDepartaments, setUserDepartaments] = useState<IUserDepartament[]>(
    []
  );
  const [userSupervisor, setUserSupervisor] = useState<IUserSupervisor[]>([]);
  const [administrador, setAdministrador] = useState(false);
  const [departamentos, setDepartamentos] = useState<IDepartamento[]>([]);
  const [showMsgDelete, setShowMsgDelete] = useState(false);
  const [showMsgDisable, setShowMsgDisable] = useState(false);
  const [disableMessage, setDisableMessage] = useState('');

  const loadDepartaments = async (): Promise<void> => {
    const response = await api.get(
      `/DepartamentoUsuario/${userSelected.seqUsuario}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    setUserDepartaments(
      response.data.data.sort((a: IUserDepartament, b: IUserDepartament) =>
        a.nomeDepartamento.localeCompare(b.nomeDepartamento)
      )
    );
  };

  const loadGroups = async (): Promise<void> => {
    const response = await api.get(`/GrupoUsuario/${userSelected.seqUsuario}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    setUserGroups(
      response.data.data.sort((a: IUserGroup, b: IUserGroup) =>
        a.nomeGrupo.localeCompare(b.nomeGrupo)
      )
    );
  };

  const loadUsers = (): void => {
    const seqUsuarioLogado = Number(
      localStorage.getItem('@LiveChat:seqUsuario')
    );

    api
      .get('/api/user', { headers: { Authorization: `Bearer ${token}` } })
      .then((response) => {
        const result = response.data;

        if (result.success) {
          setUsers(
            administrador
              ? result.data
              : result.data.filter(
                  (u: User) => u.seqUsuario === seqUsuarioLogado
                )
          );
        }
      });
  };

  const loadUsersSupervisor = async (): Promise<void> => {
    const response = await api.get(`/Supervisor/${userSelected.seqUsuario}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    setUserSupervisor(response.data.data);
  };

  const handleChangeNameUser = (seqUsuario: number, nome: string): void => {
    setUserSelected({ ...userSelected, nome });
  };

  const loadAllDepartamentos = (): void => {
    api
      .get('api/departamento', {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        const result = response.data;

        if (result.success) {
          for (let row = 0; row < result.data.length; row += 1) {
            result.data[row].exibeChatCliente =
              result.data[row].exibeChatCliente === 'S';
            result.data[row].ativo = result.data[row].ativo === 'S';
            result.data[row].corFundoLinha =
              result.data[row].corFundoLinha === 'S';
          }
          setDepartamentos(result.data);
        }
      });
  };

  const loadDepartamentos = ({ seqUsuario }: { seqUsuario: number }): void => {
    const seqUsuarioLogado = localStorage.getItem('@LiveChat:seqUsuario');

    api
      .get(
        `/departamentoUsuario/DepartamentoVinculado/${
          administrador ? seqUsuario : seqUsuarioLogado
        }`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then((response) => {
        const result = response.data;

        if (result.success) {
          for (let row = 0; row < result.data.length; row += 1) {
            result.data[row].exibeChatCliente =
              result.data[row].exibeChatCliente === 'S';
            result.data[row].ativo = result.data[row].ativo === 'S';
            result.data[row].corFundoLinha =
              result.data[row].corFundoLinha === 'S';
          }

          setUserDepartamentLinked(result.data);
          setDepartamentColor(result.data.map((row: IDepartamento) => row.cor));
        }
      });
  };

  useEffect(() => {
    setAdministrador(localStorage.getItem('@LiveChat:administrador') === 'S');
    loadUsers();
    loadAllDepartamentos();
  }, []);

  useEffect(() => {
    loadUsers();
  }, [administrador]);

  useEffect(() => {
    loadDepartamentos({ seqUsuario: userSelected.seqUsuario });
    loadGroups();
    loadDepartaments();
    loadUsersSupervisor();

    setUserColor(userSelected.cor);
  }, [userSelected]);

  const handlerSaveUserGroup = async (): Promise<void> => {
    try {
      const response = await api.post('/GrupoUsuario', userGroups, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        addToast({
          title: 'Sucesso',
          description: 'Permissão de grupo gravado com sucesso.',
          type: 'success',
        });
      } else {
        addToast({
          title: 'Erro',
          description: 'Erro ao gravar permissão de grupo.',
          type: 'error',
        });
      }
    } catch (err: any) {
      addToast({
        title: 'Erro',
        description: err.message,
        type: 'error',
      });
    }
  };

  const hadleSaveColorUser = async (): Promise<void> => {
    try {
      const seqUsuario = localStorage.getItem('@LiveChat:seqUsuario');

      const response = await api.put(
        `api/user/${seqUsuario}/userColor/${encodeURIComponent(userColor)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    } catch (err: any) {
      addToast({
        title: 'Erro',
        description: err.message,
        type: 'error',
      });
    }
  };

  const handlerSaveUserDepartament = async (): Promise<void> => {
    try {
      const response = await api.post(
        '/DepartamentoUsuario',
        userDepartaments,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        addToast({
          title: 'Sucesso',
          description: 'Permissão de departamento gravado com sucesso.',
          type: 'success',
        });
      } else {
        addToast({
          title: 'Erro',
          description: 'Erro ao gravar permissão de departamento.',
          type: 'error',
        });
      }
    } catch (err: any) {
      addToast({
        title: 'Erro',
        description: err.message,
        type: 'error',
      });
    }
  };

  const handlerSaveUserSupervisor = async (): Promise<void> => {
    try {
      const response = await api.post('/Supervisor', userSupervisor, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        addToast({
          title: 'Sucesso',
          description: 'Dados do supervisor gravados com sucesso.',
          type: 'success',
        });
      } else {
        addToast({
          title: 'Erro',
          description: 'Erro ao gravar dados do supervisor.',
          type: 'error',
        });
      }
    } catch (err: any) {
      addToast({
        title: 'Erro',
        description: err.message,
        type: 'error',
      });
    }
  };

  const handlerInativaUsuario = async (dataUser: User): Promise<void> => {
    try {
      const response = await api.patch(
        `api/user/${dataUser.seqUsuario}/inativar`,
        null,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        loadUsers();
        setShowMsgDisable(false);
        addToast({
          title: 'Sucesso',
          description: 'Usuário inativado com sucesso.',
          type: 'success',
        });
      } else {
        addToast({
          title: 'Erro',
          description: 'Erro ao inativar o usuário.',
          type: 'error',
        });
      }
    } catch (err: any) {
      addToast({
        title: 'Erro',
        description: err.message,
        type: 'error',
      });
    }
  };

  const handleDeleteUser = async (): Promise<void> => {
    try {
      const response = await api.delete(
        `/api/user/${userInativar.seqUsuario}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const apiMessage = extractApiMessage(response?.data);

      if (response?.status >= 400 && shouldOfferDisable(apiMessage)) {
        setDisableMessage(apiMessage);
        setShowMsgDisable(true);
        return;
      }

      if (response.data.success) {
        loadUsers();
        addToast({
          title: 'Sucesso',
          description: 'Usuário excluído com sucesso.',
          type: 'success',
        });
      } else {
        if (shouldOfferDisable(apiMessage)) {
          setDisableMessage(apiMessage);
          setShowMsgDisable(true);
          return;
        }

        addToast({
          title: 'Erro',
          description:
            apiMessage || response.data.message || response.data.Message,
          type: 'error',
        });
      }
    } catch (err: any) {
      const apiMessage = extractApiMessage(err?.response?.data);

      if (shouldOfferDisable(apiMessage)) {
        setDisableMessage(apiMessage);
        setShowMsgDisable(true);
      } else {
        addToast({
          title: 'Erro',
          description: apiMessage || err?.message,
          type: 'error',
        });
      }
    } finally {
      setShowMsgDelete(false);
    }
  };

  const handleSave = (dataUser: User): void => {
    loadUsers();
    setUserSelected(dataUser);
  };

  useEffect(() => {
    const departamentoSelecionado = userDepartaments.filter(
      (row: IUserDepartament) => row.membroDepartamento === 'S'
    );

    const departamentosLinkedNew: IDepartamento[] = [];
    const departamentosColorNew: string[] = [];

    departamentoSelecionado.forEach((row: IUserDepartament) => {
      const departamentoColorNew = departamentos.find(
        (d: IDepartamento) => d.seqDepartamento === row.seqDepartamento
      );
      departamentosLinkedNew.push({
        seqDepartamento: row.seqDepartamento,
        nome: row.nomeDepartamento,
        exibeChatCliente: false,
        ordemApresentacao: 0,
        ativo: true,
        cor: departamentoColorNew ? departamentoColorNew.cor : '',
        corFundoLinha: false,
      });

      departamentosColorNew.push(
        departamentoColorNew ? departamentoColorNew.cor : ''
      );
    });

    const existUserColor = departamentosColorNew.includes(userColor);
    if (!existUserColor) {
      setUserColor('#616161');
    }

    setUserDepartamentLinked(departamentosLinkedNew);
    setDepartamentColor(departamentosColorNew);
  }, [userDepartaments]);

  return (
    <div>
      <ModalMessage
        title="Excluir usuário"
        message="Isso irá excluir o usuário selecionado. Gostaria de excluí-lo?"
        handleConfirme={handleDeleteUser}
        showMessage={showMsgDelete}
        setShowMessage={setShowMsgDelete}
      />

      <ModalYesNo
        title="Atenção"
        message={disableMessage}
        show={showMsgDisable}
        onYes={() => handlerInativaUsuario(userInativar)}
        onNo={() => setShowMsgDisable(false)}
      />
      <div className="d-flex">
        <section className="col-6">
          <div
            className="card position-static ms-2"
            style={{
              borderRadius: '0.68rem',
              height: 'Calc(100vh - 16rem)',
              boxShadow: '0px 4px 6px 3px rgba(0, 0, 0, 0.25)',
            }}
          >
            <div
              className="table-responsive overflow-hidden h-100"
              style={{
                borderRadius: '0.68rem',
                borderColor: 'transparent',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <div
                className="d-flex align-items-center justify-content-between m-0 pe-2 ps-2 text-white"
                style={{
                  fontWeight: '600',
                  fontSize: '1.7rem',
                  backgroundColor: '#1A1A1A',
                }}
              >
                <span className="pb-1 pt-1 d-flex align-items-center justify-content-start">
                  Usuários
                </span>
                <Button
                  variant="primary"
                  disabled={!administrador}
                  onClick={() => {
                    setUserSelected({
                      seqUsuario: -1,
                      usuario: '',
                      nome: '',
                      email: '',
                      changePassword: true,
                      password: '',
                      confirmPassword: '',
                      ativo: 'S',
                      administrador: 'N',
                      cor: '',
                    });
                  }}
                >
                  <div className="d-flex align-items-center">
                    <FiPlus style={{ height: '1.38rem', width: '1.38rem' }} />
                    <span className="ms-1">adicionar</span>
                  </div>
                </Button>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                <table className="table table-nowrap mb-0 table-striped-custom">
                  <thead
                    className="small fw-light text-dark"
                    style={{
                      backgroundColor: '#D9D9D9',
                      borderRadius: '0.68rem!important',
                      fontSize: '1.25rem',
                    }}
                  >
                    <tr style={{ color: '#000' }}>
                      <th className="col ms-3" style={stickyHeaderCellStyle}>
                        Nome
                      </th>
                      <th className="col-1" style={stickyHeaderCellStyle}>
                        Ativo
                      </th>
                      <th className="col-2" style={stickyHeaderCellStyle}>
                        {' '}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {users
                      .sort((a, b) => a.nome.localeCompare(b.nome))
                      .map((user) => (
                        <tr
                          key={user.seqUsuario}
                          style={{
                            color: '#000',
                            fontSize: '1.25rem',
                            fontWeight: '600',
                          }}
                        >
                          <td className="col ms-1">
                            <span>{user.nome}</span>
                          </td>
                          <td className="col-1">
                            <div className="d-flex align-items-center justify-content-center pt-2">
                              {user.ativo === 'S' ? (
                                <FiCheckCircle
                                  style={{
                                    color: 'green',
                                    height: '1.8rem',
                                    width: '1.8rem',
                                  }}
                                />
                              ) : (
                                <FiXCircle
                                  style={{
                                    color: 'red',
                                    height: '1.8rem',
                                    width: '1.8rem',
                                  }}
                                />
                              )}
                            </div>
                          </td>
                          <td className="col-2">
                            <div className="d-flex justify-content-end me-2">
                              <Button
                                variant="primary m-0 p-1"
                                onClick={() => {
                                  setUserSelected(user);
                                }}
                              >
                                <Image
                                  src={PencilIcon}
                                  alt="Editar"
                                  style={{
                                    height: '1.4rem',
                                    width: '1.5rem',
                                  }}
                                />
                              </Button>
                              <Button
                                variant="secondary"
                                className="ms-2 p-1"
                                disabled={!administrador}
                              >
                                <Image
                                  src={DeleteIcon}
                                  alt="Deletar"
                                  style={{
                                    height: '1.4rem',
                                    width: '1.5rem',
                                  }}
                                  onClick={() => {
                                    setUserInativar(user);
                                    setShowMsgDelete(true);
                                  }}
                                />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
        <section className="col-6">
          <div
            className="card position-static ms-2"
            style={{
              borderRadius: '0.68rem',
              height: 'Calc(100vh - 16rem)',
              boxShadow: '0px 4px 6px 3px rgba(0, 0, 0, 0.25)',
              opacity: `${!userSelected.seqUsuario ? '0.5' : '1'}`,
            }}
          >
            <div
              className="table-responsive overflow-hidden"
              style={{ borderRadius: '0.68rem', borderColor: 'transparent' }}
            >
              {/* Cabeçalho */}
              <div className="ms-3 mt-3 d-flex">
                <div
                  style={{
                    width: '9.75rem',
                    height: '9.75rem',
                    backgroundColor:
                      userColor === '' || !userColor ? '#616161' : userColor,
                    border: '2px solid #fff',
                    borderRadius: '0.6875rem',
                    pointerEvents: `${
                      !userSelected?.seqUsuario ? 'none' : 'auto'
                    }`,
                  }}
                  className="d-flex align-items-center justify-content-center"
                >
                  <span
                    style={{
                      color: '#fff',
                      fontSize: '4.375rem',
                      textAlign: 'center',
                      fontWeight: '600',
                      wordWrap: 'break-word',
                    }}
                  >
                    {userSelected?.nome === undefined
                      ? 'NE'
                      : getInitials(userSelected?.nome)}
                  </span>
                </div>
                <div className="ms-2 d-flex flex-column justify-content-between mb-1">
                  <div>
                    <span style={{ fontSize: '1.5rem', fontWeight: '600' }}>
                      {userSelected?.nome === undefined
                        ? 'Nome Exemplo'
                        : userSelected?.nome}{' '}
                    </span>
                    <UserDepartament userDepartament={userDepartamentLinked} />
                  </div>
                  <div>
                    <span style={{ fontSize: '1.25rem', fontWeight: '600' }}>
                      Cores disponíveis
                    </span>
                    <ListColor
                      setColorSelected={setUserColor}
                      colorSelected={userColor}
                      colors={departamentColor}
                      changeColor={false}
                      positionShow="bottom"
                    />
                  </div>
                </div>
              </div>
              <div className="mt-3 ms-1 border-top border-1 pt-1">
                <Tabs
                  id="tabUserConfig"
                  activeKey={key}
                  className="ms-1"
                  onSelect={(k) => setKey(String(k))}
                >
                  <Tab
                    eventKey="user"
                    title="Usuario"
                    style={{
                      fontSize: '1.25rem',
                      height: '46vh',
                      overflow: 'scroll',
                    }}
                  >
                    <div className="ms-2">
                      <UserAdd
                        userSelected={userSelected}
                        handlerSave={handleSave}
                        handleChangeNameUser={handleChangeNameUser}
                        userColor={userColor}
                      />
                    </div>
                  </Tab>
                  {administrador && (
                    <Tab
                      eventKey="userGroup"
                      title="Grupo"
                      disabled={userSelected.seqUsuario <= 0}
                      style={{
                        fontSize: '1.25rem',
                      }}
                    >
                      <div className="ms-2 mt-2">
                        <UserGroup
                          key="userGroup"
                          userGroups={userGroups}
                          setUserGroups={setUserGroups}
                        />
                      </div>
                    </Tab>
                  )}

                  {administrador && (
                    <Tab
                      eventKey="userDepartament"
                      title="Departamento"
                      disabled={userSelected.seqUsuario <= 0}
                    >
                      <div className="ms-2 mt-2">
                        <UserDepartamentForm
                          key="userDepartament"
                          userDepartaments={userDepartaments}
                          setUserDepartaments={setUserDepartaments}
                        />
                      </div>
                    </Tab>
                  )}

                  {administrador && (
                    <Tab
                      eventKey="userSupervisor"
                      title="Supervisor"
                      disabled={userSelected.seqUsuario <= 0}
                    >
                      <div className="ms-2 mt-2">
                        <UserSupervisor
                          key="userSupervisor"
                          userSupervisor={userSupervisor}
                          setUserSupervisor={setUserSupervisor}
                        />
                      </div>
                    </Tab>
                  )}
                </Tabs>
                {key !== 'user' && (
                  <div className="d-flex justify-content-center align-items-center mt-2">
                    <Button
                      variant="secondary"
                      type="button"
                      className="mt-2 me-1"
                      style={{ fontSize: '1.25rem', fontWeight: '600' }}
                      disabled={!userSelected || !userSelected.seqUsuario}
                      onClick={() => {
                        if (key === 'userGroup') {
                          setUserGroups(
                            userGroups.map((group) => {
                              return {
                                ...group,
                                membroGrupo: 'N',
                              };
                            })
                          );
                        } else if (key === 'userDepartament') {
                          setUserDepartaments(
                            userDepartaments.map((departament) => {
                              return {
                                ...departament,
                                membroDepartamento: 'N',
                              };
                            })
                          );
                        } else if (key === 'userSupervisor') {
                          setUserSupervisor(
                            userSupervisor.map((supervisor) => {
                              return {
                                ...supervisor,
                                usuarioSubordinado: 'N',
                              };
                            })
                          );
                        }
                      }}
                    >
                      limpar dados
                    </Button>
                    <Button
                      variant="primary"
                      type="submit"
                      className="mt-2 ms-1"
                      disabled={!userSelected || !userSelected.seqUsuario}
                      style={{ fontSize: '1.25rem', fontWeight: '600' }}
                      onClick={() => {
                        handlerSaveUserGroup();
                        handlerSaveUserDepartament();
                        handlerSaveUserSupervisor();
                        hadleSaveColorUser();
                      }}
                    >
                      confirmar
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default User;
